import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { taskDeadlineLabel, taskDisplayDate } from './adminTaskDates';

export const ADMIN_TASK_STATUSES = [
  { value: 'pendiente', label: 'Pendiente' },
  { value: 'en_proceso', label: 'En proceso' },
  { value: 'finalizado', label: 'Finalizada' },
];

export default function AdminTaskEditor({ task, anchor, mode = 'edit', point, canEdit, onSave, onArchive, onClose }) {
  const dialogRef = useRef(null);
  const [title, setTitle] = useState(task.title);
  const [notes, setNotes] = useState(task.notes || '');
  const [status, setStatus] = useState(task.status || 'pendiente');
  const [targetDate, setTargetDate] = useState(String(task.target_date || task.due_date).slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useLayoutEffect(() => {
    const dialog = dialogRef.current;
    function position() {
      const rect = anchor.getBoundingClientRect();
      const width = dialog.offsetWidth;
      const height = dialog.offsetHeight;
      const left = Math.max(12, Math.min(point?.x ?? rect.left, window.innerWidth - width - 12));
      const top = Math.max(12, Math.min(point?.y ?? rect.top, window.innerHeight - height - 12));
      dialog.style.left = `${left}px`;
      dialog.style.top = `${top}px`;
    }
    dialog.showModal();
    position();
    const observer = new ResizeObserver(position);
    observer.observe(dialog);
    window.addEventListener('resize', position);
    window.addEventListener('scroll', position, true);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', position);
      window.removeEventListener('scroll', position, true);
      dialog.close();
      if (anchor.isConnected) anchor.focus({ preventScroll: true });
    };
  }, [anchor, point]);

  async function save(event) {
    event.preventDefault();
    if (!canEdit || saving || !title.trim()) return;
    setSaving(true); setError('');
    try {
      await onSave(task, { title: title.trim(), notes, status, target_date: targetDate });
      onClose();
    } catch (err) {
      setError(err.message || 'No se pudo guardar la tarea.');
      setSaving(false);
    }
  }

  async function changeStatus(value) {
    if (!canEdit || saving) return;
    setSaving(true); setError('');
    try {
      await onSave(task, { status: value });
      onClose();
    } catch (err) {
      setError(err.message || 'No se pudo cambiar el estado.');
      setSaving(false);
    }
  }

  async function archive() {
    if (!window.confirm('¿Archivar esta tarea?')) return;
    setSaving(true); setError('');
    try {
      await onArchive(task);
      onClose();
    } catch (err) {
      setError(err.message || 'No se pudo archivar la tarea.');
      setSaving(false);
    }
  }

  return createPortal(<dialog
    ref={dialogRef}
    className={`rrhh-admin-task-editor${mode === 'status' ? ' rrhh-admin-task-context' : ''}`}
    aria-labelledby="admin-task-editor-title"
    onCancel={(event) => { event.preventDefault(); if (!saving) onClose(); }}
    onClick={(event) => { if (event.target === event.currentTarget && !saving) onClose(); }}
  >
    {mode === 'status' ? <div>
      <header><h2 id="admin-task-editor-title">Cambiar estado</h2></header>
      {error && <div role="alert" className="alert alert-error">{error}</div>}
      <div role="menu" aria-label="Estado de la tarea" onKeyDown={(event) => {
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        const items = Array.from(event.currentTarget.querySelectorAll('[role="menuitemradio"]'));
        const current = items.indexOf(document.activeElement);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1 : (current + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
        items[next]?.focus();
      }}>
        {ADMIN_TASK_STATUSES.map((option) => <button type="button" role="menuitemradio" aria-checked={task.status === option.value}
          className={`status-${option.value}`} key={option.value} disabled={!canEdit || saving}
          onClick={() => changeStatus(option.value)}>{option.label}</button>)}
      </div>
    </div> : <form onSubmit={save}>
      <header>
        <h2 id="admin-task-editor-title">Tarea</h2>
        <button className="rrhh-admin-task-close" type="button" title="Cerrar" aria-label="Cerrar tarea" disabled={saving} onClick={onClose}>×</button>
      </header>
      {error && <div className="alert alert-error" role="alert">{error}</div>}
      <label htmlFor="admin-task-title">Nombre</label>
      <input id="admin-task-title" className="form-input" value={title} onChange={(event) => setTitle(event.target.value)} required readOnly={!canEdit} disabled={saving} />
      <div className="rrhh-admin-task-dates">
        <div><span>Creación</span><time dateTime={task.created_date}>{taskDisplayDate(task.created_date)}</time></div>
        <label htmlFor="admin-task-target">Fecha objetivo
          <input type="date" id="admin-task-target" className="form-input" value={targetDate} required readOnly={!canEdit} disabled={saving} onChange={(event) => setTargetDate(event.target.value)} />
        </label>
      </div>
      <span className={`rrhh-admin-task-deadline${task.overdue_days > 0 ? ' overdue' : ''}`}>{taskDeadlineLabel(task)}</span>
      <label htmlFor="admin-task-notes">Detalles / comentarios</label>
      <textarea id="admin-task-notes" className="form-input" rows={4} value={notes} onChange={(event) => setNotes(event.target.value)} readOnly={!canEdit} disabled={saving} />
      <fieldset disabled={!canEdit || saving}>
        <legend>Estado</legend>
        <div className="rrhh-admin-task-status-options">
          {ADMIN_TASK_STATUSES.map((option) => <label className={`status-${option.value}`} key={option.value}>
            <input type="radio" name="task-status" value={option.value} checked={status === option.value} onChange={() => setStatus(option.value)} />
            <span>{option.label}</span>
          </label>)}
        </div>
      </fieldset>
      <footer>
        {canEdit && <button type="button" className="btn btn-ghost" disabled={saving} onClick={archive}>Archivar</button>}
        <button type="button" className="btn btn-secondary" disabled={saving} onClick={onClose}>{canEdit ? 'Cancelar' : 'Cerrar'}</button>
        {canEdit && <button type="submit" className="btn btn-primary" disabled={saving || !title.trim()}>{saving ? 'Guardando...' : 'Guardar'}</button>}
      </footer>
    </form>}
  </dialog>, document.body);
}
